import { useMemo, useState } from 'react';
import type { PhotoCard } from '@photos/shared';
import { useUpdatePhotos } from '../../api/hooks';
import { useToast } from '../../state/toasts';
import { Modal } from '../Modal';
import { TagEditor } from '../TagEditor';

const SUGGESTED = ['group-photo', 'worship', 'fellowship', 'sermon'];

/** Edit the tags shared by every selected photo (prototype `tagDialog`). `albumId`: the album it's done from (activity log). */
export function TagsDialog({ photos, albumId, onClose, onDone }: { photos: PhotoCard[]; albumId?: string; onClose(): void; onDone(): void }) {
  const common = useMemo(
    () => (photos[0]?.tags ?? []).filter((t) => photos.every((p) => p.tags.includes(t))),
    [photos],
  );
  const [tags, setTags] = useState<string[]>(common);
  const update = useUpdatePhotos();
  const toast = useToast();
  const n = photos.length;

  const apply = () => {
    const removeTags = common.filter((t) => !tags.includes(t));
    const addTags = tags.filter((t) => !common.includes(t));
    if (!removeTags.length && !addTags.length) {
      onClose();
      return;
    }
    update.mutate(
      { ids: photos.map((p) => p.id), albumId, addTags, removeTags },
      {
        onSuccess: () => {
          onClose();
          toast({ icon: 'tag', title: `Updated tags on ${n} photos`, sub: tags.map((t) => `#${t}`).join(' ') });
          onDone();
        },
        onError: (e) => toast({ icon: 'info', title: 'Couldn’t update tags', sub: e.message }),
      },
    );
  };

  return (
    <Modal onClose={onClose} labelledBy="tags-title">
      <h3 id="tags-title">Edit tags on {n} photos</h3>
      <p className="help">
        Tags shared by all selected photos are shown. Add or remove here and it applies to every selected photo.
      </p>
      <TagEditor tags={tags} onChange={setTags} id="te" />
      <div className="sugg">
        {SUGGESTED.map((t) => (
          <button type="button" key={t} onClick={() => !tags.includes(t) && setTags([...tags, t])}>
            + #{t}
          </button>
        ))}
      </div>
      <div className="foot">
        <button type="button" className="btn" data-close onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn primary" id="ok" disabled={update.isPending} onClick={apply}>
          Apply to {n}
        </button>
      </div>
    </Modal>
  );
}
